import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { initializeIcons } from "@fluentui/react";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { PublicClientApplication, EventType, AccountInfo } from "@azure/msal-browser";
import { msalConfig, loginRequest, getToken, requiredGroupOid } from "./authConfig";
import { registerIcons } from "@fluentui/react/lib/Styling";
import { Send28Filled } from "@fluentui/react-icons";
import { HelmetProvider } from "react-helmet-async";
import * as microsoftTeams from "@microsoft/teams-js";
import { getAuthClaims } from "./api";

import "./index.css";

import Layout from "./pages/layout/Layout";
import Chat from "./pages/chat/Chat";
import LoginPage from "./pages/login/LoginPage";
import { AppStateProvider } from "./state/AppProvider";

const msalInstance = new PublicClientApplication(msalConfig);

const AccessDeniedPage = () => {
    const { instance } = useMsal();
    return (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", gap: "16px" }}>
            <p>You do not have permission to access this application. Please contact your administrator.</p>
            <div className="px-4 pb-6 flex flex-row sm:flex-row-reverse sm:px-6 gap-4">
                <a className="eq-secondary-button w-fit" onClick={() => instance.logoutRedirect()}>
                    <span className="">
                        <span>Log out</span>
                    </span>
                </a>
            </div>
        </div>
    );
};

const SecurityGroupGate = ({ children }: { children: React.ReactNode }) => {
    const [authorized, setAuthorized] = useState<boolean | null>(null);
    const client = useMsal().instance;

    useEffect(() => {
        const checkAccess = async () => {
            try {
                if (!requiredGroupOid) {
                    setAuthorized(true);
                    return;
                }
                const token = await getToken(client);
                if (!token) {
                    setAuthorized(false);
                    return;
                }
                const claims = await getAuthClaims(token.accessToken);
                setAuthorized(claims?.groups?.includes(requiredGroupOid) ?? false);
            } catch {
                setAuthorized(false);
            }
        };
        checkAccess();
    }, [client]);

    if (authorized === null) return null;
    if (!authorized) return <AccessDeniedPage />;
    return <>{children}</>;
};

// AuthGate renders Chat (wrapped in AppStateProvider) when authenticated,
// or LoginPage when not — router is defined once outside App so it is never recreated.
const AuthGate = () => {
    const isAuthenticated = useIsAuthenticated();
    return isAuthenticated ? (
        <SecurityGroupGate>
            <AppStateProvider>
                <Chat />
            </AppStateProvider>
        </SecurityGroupGate>
    ) : (
        <LoginPage />
    );
};

const router = createHashRouter([
    {
        path: "/",
        element: <Layout />,
        children: [
            { index: true, element: <AuthGate /> },
            { path: "*", lazy: () => import("./pages/NoPage") }
        ]
    }
]);

const App = () => {
    // Attempt Teams SSO on mount. If the app is not running inside Teams,
    // or if SSO fails, we fall through silently and show the login page.
    useEffect(() => {
        const tryTeamsSso = async () => {
            console.log("[Teams SSO] Starting Teams SSO attempt...");
            console.log("[Teams SSO] User agent:", navigator.userAgent);
            console.log("[Teams SSO] Current URL:", window.location.href);

            try {
                console.log("[Teams SSO] Calling microsoftTeams.app.initialize()...");
                await microsoftTeams.app.initialize();
                console.log("[Teams SSO] Teams SDK initialized successfully — app is running inside Teams.");

                console.log("[Teams SSO] Calling microsoftTeams.app.getContext()...");
                const context = await microsoftTeams.app.getContext();
                console.log("[Teams SSO] Teams context received:", JSON.stringify(context, null, 2));

                const loginHint = context.user?.loginHint ?? context.user?.userPrincipalName;
                console.log("[Teams SSO] loginHint:", loginHint ?? "(undefined — no user info in context)");

                if (!loginHint) {
                    console.warn("[Teams SSO] No loginHint available in context; proceeding with ssoSilent without it.");
                }

                console.log("[Teams SSO] Calling msalInstance.ssoSilent with loginRequest scopes:", loginRequest.scopes);
                const result = await msalInstance.ssoSilent({ ...loginRequest, loginHint });
                console.log("[Teams SSO] ssoSilent succeeded. Account:", result?.account?.username);
                msalInstance.setActiveAccount(result.account);
                console.log("[Teams SSO] Active account set. SSO complete.");
            } catch (err: unknown) {
                // Not in Teams or SSO failed — user will see the login page with the Login button
                const error = err as Error;
                console.warn("[Teams SSO] SSO failed (this is expected outside of Teams).");
                console.warn("[Teams SSO] Error name:", error?.name);
                console.warn("[Teams SSO] Error message:", error?.message);
                console.warn("[Teams SSO] Full error:", err);
            }
        };
        tryTeamsSso();
    }, []);

    // Register a single LOGIN_SUCCESS callback with proper cleanup
    useEffect(() => {
        const callbackId = msalInstance.addEventCallback(event => {
            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                msalInstance.setActiveAccount(event.payload as AccountInfo);
            }
        });
        return () => {
            if (callbackId) msalInstance.removeEventCallback(callbackId);
        };
    }, []);

    return (
        <React.StrictMode>
            <HelmetProvider>
                <MsalProvider instance={msalInstance}>
                    <RouterProvider router={router} />
                </MsalProvider>
            </HelmetProvider>
        </React.StrictMode>
    );
};

initializeIcons();

registerIcons({
    icons: {
        Send28Filled: <Send28Filled />
    }
});

// Initialize MSAL before rendering — required by MSAL v3
(async () => {
    await msalInstance.initialize();
    await msalInstance.handleRedirectPromise();
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(<App />);
})();
